import { useState, useEffect, useMemo, useRef } from 'react';
import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface EbookPreviewModalProps {
  template: EbookTemplate;
  onClose: () => void;
  onUse: () => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  allTemplates: EbookTemplate[];
  onPreview: (t: EbookTemplate) => void;
}

interface SamplePage {
  id: string;
  label: string;
  type: 'cover' | 'toc' | 'intro' | 'chapter' | 'visual' | 'summary';
}

function generateSamplePages(template: EbookTemplate): SamplePage[] {
  return [
    { id: 'cover', label: 'Cover', type: 'cover' },
    { id: 'toc', label: 'Contents', type: 'toc' },
    { id: 'intro', label: 'Introduction', type: 'intro' },
    { id: 'chapter1', label: 'Chapter 1', type: 'chapter' },
    { id: 'visual', label: 'Visuals', type: 'visual' },
    { id: 'summary', label: 'Summary', type: 'summary' },
  ];
}

function CoverPage({ template }: { template: EbookTemplate }) {
  return (
    <div className="w-full h-full flex flex-col" style={{ background: `linear-gradient(135deg, ${template.color}22 0%, ${template.color}08 100%)` }}>
      <div className="flex-1 relative overflow-hidden">
        <img
          src={template.image}
          alt={template.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-3" style={{ backgroundColor: template.color }}>
            {template.category}
          </div>
          <h2 className="text-3xl font-extrabold text-white mb-2 leading-tight">{template.name}</h2>
          <p className="text-white/75 text-sm mb-4">{template.description}</p>
          <div className="flex items-center gap-4 text-white/70 text-xs">
            <span className="flex items-center gap-1"><i className="ri-book-open-line"></i> {template.chapters} Chapters</span>
            <span className="flex items-center gap-1"><i className="ri-pages-line"></i> {template.pages} Pages</span>
            <span className="flex items-center gap-1"><i className="ri-star-fill text-amber-400"></i> {template.rating}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TocPage({ template }: { template: EbookTemplate }) {
  const sampleChapters = [
    { num: 1, title: 'Introduction & Foundation', pages: 12 },
    { num: 2, title: 'Core Principles & Frameworks', pages: 18 },
    { num: 3, title: 'Deep Dive: Strategies That Work', pages: 22 },
    { num: 4, title: 'Practical Implementation', pages: 20 },
    { num: 5, title: 'Case Studies & Real Examples', pages: 25 },
    { num: 6, title: 'Advanced Techniques', pages: 19 },
    { num: 7, title: 'Overcoming Challenges', pages: 16 },
    { num: 8, title: 'Measuring Success', pages: 14 },
    { num: 9, title: 'Next Steps & Action Plan', pages: 12 },
    { num: 10, title: 'Resources & References', pages: 8 },
  ];

  return (
    <div className="p-8 h-full flex flex-col bg-white">
      <div className="mb-6">
        <div className="w-8 h-1 rounded-full mb-3" style={{ backgroundColor: template.color }}></div>
        <h2 className="text-2xl font-extrabold text-slate-900">Table of Contents</h2>
        <p className="text-sm text-slate-500 mt-1">{template.name}</p>
      </div>
      <div className="flex-1 space-y-1 overflow-hidden">
        {sampleChapters.slice(0, Math.min(template.chapters, 10)).map((ch) => (
          <div key={ch.num} className="flex items-center gap-3 py-2 border-b border-slate-100 group">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: template.color }}>
              {ch.num}
            </div>
            <span className="flex-1 text-sm text-slate-700 font-medium line-clamp-1">{ch.title}</span>
            <span className="text-xs text-slate-400 flex-shrink-0 font-mono">{ch.pages}p</span>
          </div>
        ))}
        {template.chapters > 10 && (
          <div className="text-center py-2">
            <span className="text-xs text-slate-400 font-medium">+ {template.chapters - 10} more chapters</span>
          </div>
        )}
      </div>
    </div>
  );
}

function IntroPage({ template }: { template: EbookTemplate }) {
  return (
    <div className="p-8 h-full flex flex-col bg-white">
      <div className="mb-5">
        <div className="w-8 h-1 rounded-full mb-3" style={{ backgroundColor: template.color }}></div>
        <h2 className="text-xl font-extrabold text-slate-900">Introduction</h2>
      </div>
      <div className="flex-1 overflow-hidden">
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Welcome to <strong className="text-slate-900">{template.name}</strong>. This comprehensive guide has been crafted to provide you with actionable insights, proven frameworks, and real-world strategies that you can apply immediately.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Throughout this eBook, you&apos;ll discover transformative concepts backed by research and validated through thousands of real-world applications. Each chapter builds on the previous, creating a cohesive learning journey.
        </p>
        <div className="bg-slate-50 rounded-xl p-4 mb-4 border-l-4" style={{ borderColor: template.color }}>
          <p className="text-sm font-semibold text-slate-800 mb-1">What You&apos;ll Learn</p>
          <ul className="space-y-1.5 mt-2">
            {['Core foundations and key principles', 'Actionable step-by-step strategies', 'Expert insights and case studies', 'Tools and frameworks for success'].map((item) => (
              <li key={item} className="flex items-start gap-2 text-xs text-slate-600">
                <i className="ri-checkbox-circle-fill mt-0.5 flex-shrink-0" style={{ color: template.color }}></i>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: 'ri-book-open-line', label: 'Chapters', val: template.chapters },
            { icon: 'ri-pages-line', label: 'Pages', val: template.pages },
            { icon: 'ri-time-line', label: 'Est. Read', val: `${Math.round(template.pages / 25)}h` },
          ].map((stat) => (
            <div key={stat.label} className="text-center p-3 rounded-xl bg-slate-50">
              <div className="w-8 h-8 flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: template.color }}>
                <i className={`${stat.icon} text-lg`}></i>
              </div>
              <div className="text-lg font-extrabold text-slate-900">{stat.val}</div>
              <div className="text-xs text-slate-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChapterPage({ template }: { template: EbookTemplate }) {
  return (
    <div className="p-8 h-full flex flex-col bg-white">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-extrabold text-sm" style={{ backgroundColor: template.color }}>
          01
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: template.color }}>Chapter One</div>
          <h2 className="text-lg font-extrabold text-slate-900">Core Foundations</h2>
        </div>
      </div>
      <div className="flex-1 overflow-hidden space-y-3">
        <p className="text-sm text-slate-600 leading-relaxed">
          The foundation of any successful approach lies in understanding the core principles that govern outcomes. In this opening chapter, we establish the essential frameworks that will guide your journey.
        </p>
        <div className="space-y-2">
          {['The 80/20 Rule Applied to Success', 'Building a Growth-Oriented Mindset', 'The Three Pillars Framework', 'Common Pitfalls to Avoid'].map((section, i) => (
            <div key={section} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: template.color + 'cc' }}>
                {i + 1}
              </div>
              <span className="text-sm font-medium text-slate-700">{section}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto p-3 rounded-xl text-sm" style={{ backgroundColor: template.color + '12' }}>
          <p className="font-semibold mb-1" style={{ color: template.color }}>Key Insight</p>
          <p className="text-slate-600 text-xs leading-relaxed">Success is rarely the result of a single breakthrough — it&apos;s the compound effect of consistent small actions applied with intention over time.</p>
        </div>
      </div>
    </div>
  );
}

function VisualPage({ template }: { template: EbookTemplate }) {
  const bars = [65, 82, 48, 91, 73, 58, 87];
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];

  return (
    <div className="p-8 h-full flex flex-col bg-white">
      <div className="mb-5">
        <div className="w-8 h-1 rounded-full mb-3" style={{ backgroundColor: template.color }}></div>
        <h2 className="text-lg font-extrabold text-slate-900">Data & Insights</h2>
        <p className="text-xs text-slate-500 mt-0.5">Visual overview of key metrics and trends</p>
      </div>
      <div className="flex-1 space-y-5">
        {/* Bar Chart */}
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-600 mb-3">Progress Metrics</p>
          <div className="flex items-end gap-2 h-24">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-sm transition-all" style={{ height: `${h}%`, backgroundColor: template.color, opacity: 0.7 + (i * 0.04) }}></div>
                <span className="text-xs text-slate-400">{labels[i]}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Success Rate', val: '94%', trend: '+12%' },
            { label: 'Avg. ROI', val: '3.8x', trend: '+0.5x' },
            { label: 'Time Saved', val: '6hrs/wk', trend: '+2hrs' },
            { label: 'Satisfaction', val: '4.9/5', trend: '+0.3' },
          ].map((stat) => (
            <div key={stat.label} className="p-3 rounded-xl" style={{ backgroundColor: template.color + '0d' }}>
              <div className="text-xs text-slate-500 mb-1">{stat.label}</div>
              <div className="text-xl font-extrabold" style={{ color: template.color }}>{stat.val}</div>
              <div className="text-xs text-emerald-600 font-medium">{stat.trend} vs last year</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummaryPage({ template }: { template: EbookTemplate }) {
  return (
    <div className="p-8 h-full flex flex-col bg-white">
      <div className="mb-5">
        <div className="w-8 h-1 rounded-full mb-3" style={{ backgroundColor: template.color }}></div>
        <h2 className="text-lg font-extrabold text-slate-900">Key Takeaways</h2>
        <p className="text-xs text-slate-500 mt-0.5">Your action plan for success</p>
      </div>
      <div className="flex-1 space-y-3 overflow-hidden">
        {[
          { icon: 'ri-lightbulb-line', title: 'Core Insight', desc: 'Apply the foundational principles consistently to build lasting momentum in your journey.' },
          { icon: 'ri-rocket-line', title: 'Action Steps', desc: 'Start with the three key actions outlined in Chapter 4 — small steps lead to massive results.' },
          { icon: 'ri-tools-line', title: 'Your Toolkit', desc: 'Use the frameworks and templates provided to systematically approach every challenge.' },
          { icon: 'ri-trophy-line', title: 'Success Metrics', desc: 'Track your progress using the measurement system in Chapter 8 to stay on course.' },
        ].map((item) => (
          <div key={item.title} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white" style={{ backgroundColor: template.color }}>
              <i className={`${item.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">{item.title}</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.desc}</p>
            </div>
          </div>
        ))}
        <div className="mt-auto p-4 rounded-xl text-center" style={{ background: `linear-gradient(135deg, ${template.color}20, ${template.color}10)` }}>
          <p className="text-sm font-extrabold text-slate-900 mb-1">Ready to get started?</p>
          <p className="text-xs text-slate-500">Use the AI eBook Creator to customize this template with your unique content and style.</p>
        </div>
      </div>
    </div>
  );
}

function SimilarTemplatesStrip({
  current,
  allTemplates,
  onPreview,
}: {
  current: EbookTemplate;
  allTemplates: EbookTemplate[];
  onPreview: (t: EbookTemplate) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const similar = useMemo<EbookTemplate[]>(() => {
    const sameCategory = allTemplates.filter(
      t => t.id !== current.id && t.category === current.category
    );
    const sameTagsScore = (t: EbookTemplate) =>
      t.tags.filter(tag => current.tags.includes(tag)).length;
    return [...sameCategory]
      .sort((a, b) => sameTagsScore(b) - sameTagsScore(a) || b.rating - a.rating)
      .slice(0, 6);
  }, [current, allTemplates]);

  if (similar.length === 0) return null;

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === 'right' ? 280 : -280, behavior: 'smooth' });
  };

  return (
    <div className="flex-shrink-0 border-t border-slate-100 bg-slate-50/70 px-6 py-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-sparkling-line text-amber-500 text-sm"></i>
          </div>
          <span className="text-xs font-extrabold text-slate-700 uppercase tracking-wide">Similar Templates</span>
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">AI-matched</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => scroll('left')}
            className="w-6 h-6 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-white transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-6 h-6 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-white transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {similar.map(t => (
          <button
            key={t.id}
            onClick={() => onPreview(t)}
            className="group flex-shrink-0 w-36 bg-white rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:shadow-md transition-all duration-200 overflow-hidden cursor-pointer text-left"
          >
            <div className="relative overflow-hidden" style={{ aspectRatio: '3/4', maxHeight: '108px' }}>
              <img
                src={t.image}
                alt={t.name}
                className="w-full h-full object-cover object-top transition-transform duration-400 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
              {/* Common tags badge */}
              <div className="absolute bottom-1.5 left-2">
                <div className="flex items-center gap-1">
                  {t.tags
                    .filter(tag => current.tags.includes(tag))
                    .slice(0, 1)
                    .map(tag => (
                      <span
                        key={tag}
                        className="px-1.5 py-0.5 bg-white/90 text-slate-700 text-xs font-bold rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                </div>
              </div>
              <div
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ backgroundColor: t.color }}
              ></div>
            </div>
            <div className="p-2.5">
              <p className="text-xs font-bold text-slate-800 line-clamp-2 leading-snug group-hover:text-amber-700 transition-colors mb-1.5">
                {t.name}
              </p>
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-amber-400 text-xs"></i>
                <span className="text-xs font-semibold text-slate-600">{t.rating}</span>
                <span className="text-xs text-slate-400 ml-auto">{t.chapters}ch</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function EbookPreviewModal({ template, onClose, onUse, isFavorite, onToggleFavorite, allTemplates, onPreview }: EbookPreviewModalProps) {
  const [activeTab, setActiveTab] = useState<'preview' | 'details'>('preview');
  const [activePage, setActivePage] = useState(0);
  const samplePages = generateSamplePages(template);

  // Reset page when template changes
  useEffect(() => {
    setActivePage(0);
    setActiveTab('preview');
  }, [template.id]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const renderPage = () => {
    const page = samplePages[activePage];
    switch (page.type) {
      case 'cover': return <CoverPage template={template} />;
      case 'toc': return <TocPage template={template} />;
      case 'intro': return <IntroPage template={template} />;
      case 'chapter': return <ChapterPage template={template} />;
      case 'visual': return <VisualPage template={template} />;
      case 'summary': return <SummaryPage template={template} />;
      default: return <CoverPage template={template} />;
    }
  };

  const BADGE_COLORS: Record<string, string> = {
    Popular: 'bg-amber-100 text-amber-700',
    New: 'bg-emerald-100 text-emerald-700',
    Trending: 'bg-rose-100 text-rose-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: template.color }}>
              <i className="ri-book-2-line text-base"></i>
            </div>
            <div>
              <h3 className="font-extrabold text-slate-900 text-base leading-tight line-clamp-1">{template.name}</h3>
              <p className="text-xs text-slate-500">{template.category}</p>
            </div>
            {template.badge && (
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${BADGE_COLORS[template.badge] || ''}`}>
                {template.badge}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onToggleFavorite(template.id)}
              className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all cursor-pointer ${
                isFavorite ? 'bg-rose-500 border-rose-500 text-white' : 'border-slate-200 text-slate-400 hover:border-rose-300 hover:text-rose-500'
              }`}
            >
              <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center px-6 border-b border-slate-100 flex-shrink-0">
          {(['preview', 'details'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-semibold capitalize transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? 'border-b-2 text-slate-900'
                  : 'text-slate-400 hover:text-slate-600 border-b-2 border-transparent'
              }`}
              style={activeTab === tab ? { borderColor: template.color } : {}}
            >
              {tab === 'preview' ? 'Sample Pages' : 'Template Details'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden min-h-0">
          {activeTab === 'preview' ? (
            <div className="flex h-full">
              {/* Thumbnail Strip */}
              <div className="w-28 border-r border-slate-100 bg-slate-50 flex-shrink-0 overflow-y-auto p-3 space-y-2">
                {samplePages.map((page, idx) => (
                  <button
                    key={page.id}
                    onClick={() => setActivePage(idx)}
                    className={`w-full rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      activePage === idx ? 'shadow-lg scale-105' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    style={activePage === idx ? { borderColor: template.color } : {}}
                  >
                    <div className="w-full aspect-[3/4] bg-gradient-to-br from-slate-100 to-white flex items-center justify-center relative overflow-hidden">
                      {idx === 0 ? (
                        <img src={template.image} alt={page.label} className="w-full h-full object-cover" />
                      ) : (
                        <div className="p-1.5 w-full h-full flex flex-col">
                          <div className="w-full h-1.5 rounded-full mb-1.5" style={{ backgroundColor: template.color }}></div>
                          <div className="space-y-1 flex-1">
                            {[80, 60, 70, 55, 65].map((w, i) => (
                              <div key={i} className="h-1 rounded-full bg-slate-200" style={{ width: `${w}%` }}></div>
                            ))}
                          </div>
                        </div>
                      )}
                      {activePage === idx && (
                        <div className="absolute inset-0 ring-2 ring-inset rounded-xl" style={{ borderColor: template.color + '60' }}></div>
                      )}
                    </div>
                    <div className={`py-1 px-1 text-center text-xs font-semibold transition-colors ${activePage === idx ? 'text-white' : 'text-slate-500'}`}
                      style={activePage === idx ? { backgroundColor: template.color } : {}}>
                      {page.label}
                    </div>
                  </button>
                ))}
              </div>

              {/* Page Preview */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-hidden bg-slate-100 flex items-center justify-center p-6">
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{ width: '360px', height: '480px' }}>
                    {renderPage()}
                  </div>
                </div>
                {/* Navigation */}
                <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between bg-white flex-shrink-0">
                  <button
                    onClick={() => setActivePage(p => Math.max(0, p - 1))}
                    disabled={activePage === 0}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-base"></i>
                  </button>
                  <span className="text-xs text-slate-500 font-medium">
                    Page <span className="font-bold text-slate-800">{activePage + 1}</span> of <span className="font-bold">{samplePages.length}</span>
                    <span className="mx-2 text-slate-300">·</span>
                    {samplePages[activePage].label}
                  </span>
                  <button
                    onClick={() => setActivePage(p => Math.min(samplePages.length - 1, p + 1))}
                    disabled={activePage === samplePages.length - 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 overflow-y-auto h-full">
              <div className="grid grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-5">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">About This Template</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{template.description}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Style Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {template.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Color Accent</h4>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl shadow-md" style={{ backgroundColor: template.color }}></div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">{template.color.toUpperCase()}</p>
                        <p className="text-xs text-slate-500">Primary accent color</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right Column */}
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: 'ri-book-open-line', label: 'Chapters', val: template.chapters },
                      { icon: 'ri-pages-line', label: 'Pages', val: template.pages },
                      { icon: 'ri-star-fill', label: 'Rating', val: `${template.rating}★` },
                      { icon: 'ri-download-line', label: 'Downloads', val: `${(template.downloads / 1000).toFixed(1)}k` },
                    ].map(stat => (
                      <div key={stat.label} className="p-4 bg-slate-50 rounded-xl text-center">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2" style={{ color: template.color }}>
                          <i className={`${stat.icon} text-xl`}></i>
                        </div>
                        <p className="text-xl font-extrabold text-slate-900">{stat.val}</p>
                        <p className="text-xs text-slate-500">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">What&apos;s Included</h4>
                    <div className="space-y-2">
                      {[
                        'Full chapter structure & outlines',
                        'Professional cover page design',
                        'Table of contents template',
                        'Chapter intro & summary sections',
                        'Visual data page templates',
                        'Action plan & CTA sections',
                        'Export to PDF & EPUB',
                        'Custom branding options',
                      ].map(item => (
                        <div key={item} className="flex items-center gap-2.5 text-sm text-slate-600">
                          <i className="ri-checkbox-circle-fill text-emerald-500 flex-shrink-0"></i>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Similar Templates Strip ───────────────────────────── */}
        <SimilarTemplatesStrip
          current={template}
          allTemplates={allTemplates}
          onPreview={onPreview}
        />

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-amber-400"></i>
              <strong className="text-slate-800">{template.rating}</strong> rating
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-download-line text-emerald-500"></i>
              <strong className="text-slate-800">{template.downloads.toLocaleString()}</strong> uses
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onToggleFavorite(template.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isFavorite ? 'bg-rose-50 border-rose-300 text-rose-700' : 'border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-600'
              }`}
            >
              <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
              {isFavorite ? 'Saved' : 'Save'}
            </button>
            <button
              onClick={onUse}
              className="flex items-center gap-2 px-6 py-2 rounded-xl text-sm font-bold text-white transition-all cursor-pointer whitespace-nowrap shadow-lg"
              style={{ background: `linear-gradient(135deg, ${template.color}, ${template.color}cc)` }}
            >
              <i className="ri-edit-line text-base"></i>
              Use This Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
